import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { TwScope } from "../../../components/ui/TwScope";
import IngredientRegisterModalView from "../../ingredients/ingredientRegister/IngredientRegisterModalView";
import ProductDetailModal from "../productDetails/ProductDetailsModal";
import ProductEditModal from "../productEdit/ProductEditModal";
import ProductRegisterModal from "../productRegister/ProductRegisterModal";
import LoadingOverlay from "./components/LoadingOverlay";
import ProductListHeader from "./components/ProductListHeader";
import ProductListPagination from "./components/ProductListPagination";
import ProductListToolbar from "./components/ProductListToolbar";
import ProductTable from "./components/ProductTable";
import { useProductListViewModel } from "./hooks/useProductListViewModel";

/**
 * Tailwind rebuild of the Product grid, matching the pattern established by
 * `IngredientListNew`
 * (src/screens/lcaIngredients/ingredientList/IngredientListNew.tsx): same
 * `TwScope` page shell, same header/toolbar/table/pagination composition,
 * all state/data-fetching living in `useProductListViewModel` so this
 * component only composes presentation.
 *
 * Gated behind `ENABLE_LCA_PRODUCT_UI_REDESIGN` — see
 * src/routes/productRoutes.tsx for the switch against the untouched legacy
 * `ProductList` (src/screens/product/productList/ProductList.tsx).
 *
 * The Register, Details and Edit modals below are all Tailwind rebuilds —
 * src/screens/lcaProduct/productRegister/ProductRegisterModal.tsx,
 * src/screens/lcaProduct/productDetails/ProductDetailsModal.tsx,
 * src/screens/lcaProduct/productEdit/ProductEditModal.tsx — completing
 * the Product screens' migration to this new top-level folder, following
 * the same convention `lcaIngredients` was built under.
 */
const ProductListNew = () => {
  const viewModel = useProductListViewModel();

  return (
    <TwScope className="tw-min-h-full tw-bg-white tw-p-6">
      <ProductListHeader onRegisterClick={viewModel.openRegisterModal} />

      <ProductListToolbar
        searchKey={viewModel.searchKey}
        onSearchChange={viewModel.setSearchKey}
        refetchWithFilters={viewModel.refetchWithFilters}
      />

      <ProductTable
        rows={viewModel.rows}
        isLoading={viewModel.isRowsLoading}
        onView={viewModel.openDetailsModal}
        onEdit={viewModel.openEditModal}
        onDelete={viewModel.openDeleteConfirm}
      />

      <ProductListPagination
        totalCount={viewModel.totalCount}
        pageNumber={viewModel.pageNumber}
        totalPages={viewModel.totalPages}
        onPageChange={viewModel.handlePagination}
      />

      <ProductRegisterModal
        visibleModal={viewModel.visibleRegisterModal}
        setVisibleModal={viewModel.handleNavigationRegister}
      />

      {viewModel.visibleDetailsModal ? (
        <ProductDetailModal
          visibleModal={viewModel.visibleDetailsModal}
          setVisibleModal={() => viewModel.closeDetailsModal()}
          productId={viewModel.productId}
          openEditDetails={viewModel.navigateDetailsToEdit}
        />
      ) : null}

      {viewModel.visibleEditModal ? (
        <ProductEditModal
          visibleModal={viewModel.visibleEditModal}
          setVisibleModal={viewModel.closeEditModal}
          productId={viewModel.productId}
        />
      ) : null}

      {viewModel.visibleRegModal ? (
        <IngredientRegisterModalView
          state={viewModel.dataToPass}
          visibleModal={viewModel.visibleRegModal}
          setVisibleModal={viewModel.closeIngredientRegisterModal}
        />
      ) : null}

      <ConfirmModal
        viewModal={viewModel.visibleDeleteConfirm}
        closeModal={viewModel.closeDeleteConfirm}
        cancel={viewModel.closeDeleteConfirm}
        approve={viewModel.confirmDelete}
        title="Delete Product Data"
        subTitle="Are you sure you want to remove this product?"
      />

      {viewModel.isMutationInFlight ? <LoadingOverlay /> : null}
    </TwScope>
  );
};

export default ProductListNew;
